import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Download, CalendarIcon, Eye, Clock, Moon, Sun, Calendar as CalendarDays } from 'lucide-react';
import { format, parseISO, startOfWeek, addDays } from 'date-fns';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { calculateNightMinutes } from '@/pages/blank-page/utils/dateTimeUtils';
import { shouldExcludeSchedule } from '../services/utils/hoursCalculationUtils';
import { isHoliday, loadHolidaysForDateRange, splitShiftByHolidaysSync } from '../services/utils/holidayUtils';
import { isLeave } from '@/pages/blank-page/utils/leaveUtils';
import * as XLSX from 'xlsx';
import { calculateShiftDuration, formatHoursAndMinutes, calculateShiftDurationInMinutes } from '@/pages/schedule-alerts/services/utils/dateUtils';

interface Guard {
  id: string;
  full_name: string;
  first_name?: string;
  last_name?: string;
  code: string;
  status: string;
}

interface Schedule {
  id: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  location: string;
  assigned_guards: string[];
  team: { name: string };
}

interface DayStats {
  date: string;
  totalHours: number;
  nightHours: number;
  weekendHours: number;
  overtimeHours: number;
  schedules: Schedule[];
}

export const EmployeeScheduleViewer: React.FC = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [guards, setGuards] = useState<Guard[]>([]);
  const [selectedGuard, setSelectedGuard] = useState<string>('');
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [reportType, setReportType] = useState<string>('shifts');
  const [includeDetails, setIncludeDetails] = useState<string[]>(['hours', 'overtime']);
  const [scheduleData, setScheduleData] = useState<DayStats[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchGuards();
  }, []);

  const fetchGuards = async () => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setGuards([]);
        return;
      }

      // Check if user is super admin
      const { data: superAdminCheck } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'super_admin')
        .single();

      const isSuperAdmin = !!superAdminCheck;

      let query = supabase
        .from('profiles')
        .select('id, full_name, first_name, last_name, code, status, created_by')
        .in('status', ['active', 'archived']);

      // If not super admin, filter to only show users created by this admin or their admin group
      if (!isSuperAdmin) {
        // Get admin group members (parent + contributors)
        const { data: adminGroup } = await supabase
          .from('admin_team')
          .select('member_id')
          .eq('admin_id', user.id);

        const adminGroupIds = adminGroup?.map(ag => ag.member_id) || [user.id];

        // Only show users created by members of the admin group
        query = query.in('created_by', adminGroupIds);
      }

      const { data, error } = await query
        .order('status', { ascending: true }) // Show active first, then archived
        .order('last_name', { ascending: true })
        .order('first_name', { ascending: true });

      if (error) throw error;
      setGuards(data || []);
    } catch (error) {
      console.error('Error fetching guards:', error);
      toast({
        variant: "destructive",
        title: t("error") || "Error",
        description: t("errorFetchingGuards") || "Error fetching guards",
      });
    }
  };

  // Formatted guards for display (Last name, First name)
  const formattedGuards = useMemo(() => {
    return guards.map(guard => ({
      ...guard,
      displayName: guard.last_name && guard.first_name 
        ? `${guard.last_name}, ${guard.first_name}${guard.status === 'archived' ? ' (ARCHIVED)' : ''}`
        : `${guard.full_name || 'Άγνωστος'}${guard.status === 'archived' ? ' (ARCHIVED)' : ''}`
    }));
  }, [guards]);

  const [comboOpen, setComboOpen] = useState(false);

  // Function to get guard schedules for a specific day
  const getGuardSchedulesForDay = (guardId: string, date: Date, schedules: Schedule[]) => {
    return schedules.filter(schedule => {
      if (!schedule.assigned_guards?.includes(guardId)) return false;
      const scheduleStart = fromLocalISOString(schedule.start_date);
      const targetDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const nextDay = new Date(targetDay);
      nextDay.setDate(nextDay.getDate() + 1);
      // Only include schedules that START on this day to avoid overnight duplicates
      return (scheduleStart >= targetDay && scheduleStart < nextDay);
    });
  };

  const calculateDayStats = (daySchedules: Schedule[], date: Date): DayStats => {
    let totalHours = 0;
    let nightHours = 0;
    let weekendHours = 0;
    let overtimeHours = 0;

    // Filter out excluded schedules (REPO, OFF, etc.) for work hours calculation
    const workSchedules = daySchedules.filter(schedule => !shouldExcludeSchedule(schedule.title));

    workSchedules.forEach(schedule => {
      const startTime = parseISO(schedule.start_date);
      const endTime = parseISO(schedule.end_date);
      const minutes = calculateShiftDurationInMinutes(startTime, endTime);
      
      totalHours += minutes / 60; // Convert back to hours for calculation

      // Calculate actual night hours (22:00 - 06:00) using proper calculation
      const nightMinutes = calculateNightMinutes(startTime, endTime);
      nightHours += nightMinutes / 60;

      // Calculate holiday hours precisely - only the hours that fall on holidays
      // But exclude leave schedules - if it's a leave during a holiday, don't count as holiday hours
      const { holidayMinutes } = splitShiftByHolidaysSync(startTime, endTime);
      const isScheduleLeave = isLeave(schedule.title);
      
      if (!isScheduleLeave) {
        weekendHours += holidayMinutes / 60;
      }
    });

    // Overtime is calculated weekly (Mon-Sun) exceeding 40h, so no per-day overtime here
    overtimeHours = 0;

    return {
      date: format(date, 'yyyy-MM-dd'),
      totalHours,
      nightHours,
      weekendHours,
      overtimeHours,
      schedules: daySchedules // Keep all schedules for display, but only work schedules for calculations
    };
  };

  const fetchScheduleData = async () => {
    if (!selectedGuard || !startDate || !endDate) {
      toast({
        variant: "destructive",
        title: t("error") || "Error",
        description: t("pleaseSelectAllFields") || "Please select guard and date range",
      });
      return;
    }

    try {
      setIsLoading(true);
      setIsViewing(true);

      // Load holidays for the date range first - this populates the cache
      console.log('Loading holidays for date range:', startDate.toISOString(), 'to', endDate.toISOString());
      await loadHolidaysForDateRange(startDate, endDate);

      // Extend the date range to catch schedules that might start earlier but extend into our range
      const extendedStartDate = new Date(startDate);
      extendedStartDate.setDate(extendedStartDate.getDate() - 2); // Look 2 days back
      extendedStartDate.setHours(0, 0, 0, 0);
      
      const extendedEndDate = new Date(endDate);
      extendedEndDate.setDate(extendedEndDate.getDate() + 2); // Look 2 days ahead
      extendedEndDate.setHours(23, 59, 59, 999);

      console.log('Fetching schedules for employee viewer:');
      console.log('Extended date range:', extendedStartDate.toISOString(), 'to', extendedEndDate.toISOString());
      console.log('Original date range:', startDate.toISOString(), 'to', endDate.toISOString());

      // Fetch ALL schedules that could potentially overlap with our date range
      const { data, error } = await supabase
        .from('team_schedules')
        .select(`
          id,
          title,
          description,
          start_date,
          end_date,
          location,
          assigned_guards,
          team:teams(name)
        `)
        .contains('assigned_guards', [selectedGuard])
        .lte('start_date', extendedEndDate.toISOString())
        .gte('end_date', extendedStartDate.toISOString())
        .order('start_date');

      if (error) throw error;

      console.log('Fetched schedules:', data);

      // Generate all days in the selected range (not extended range)
      const dayStats: DayStats[] = [];
      const currentDate = new Date(startDate);
      currentDate.setHours(0, 0, 0, 0);
      const endDateTime = new Date(endDate);
      endDateTime.setHours(23, 59, 59, 999);

      while (currentDate <= endDate) {
        // Find all schedules for this guard on this specific day
        const daySchedules = getGuardSchedulesForDay(selectedGuard, currentDate, data || []);
        const stats = calculateDayStats(daySchedules, currentDate);
        dayStats.push(stats);
        currentDate.setDate(currentDate.getDate() + 1);
      }

      setScheduleData(dayStats);
    } catch (error) {
      console.error('Error fetching schedule data:', error);
      toast({
        variant: "destructive",
        title: t("error") || "Error",
        description: t("errorFetchingSchedules") || "Error fetching schedules",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleIncludeDetailsChange = (value: string) => {
    setIncludeDetails(prev => 
      prev.includes(value) 
        ? prev.filter(item => item !== value)
        : [...prev, value]
    );
  };

  const handleExportPDF = async () => {
    if (!isViewing || scheduleData.length === 0 || !reportRef.current || !startDate || !endDate) return;
    const module = await import('html2pdf.js');
    const html2pdf = (module as any).default || (module as any);
    const fileName = `${selectedGuardName || 'employee'}_${format(startDate, 'yyyyMMdd')}_${format(endDate, 'yyyyMMdd')}.pdf`;
    const opt = {
      margin: 10,
      filename: fileName,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().set(opt).from(reportRef.current).save();
  };

  const handleExportExcel = () => {
    if (!isViewing || scheduleData.length === 0 || !startDate || !endDate) return;

    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = [
      ['Employee', selectedGuardName || ''],
      ['Employee Code', selectedGuardCode || ''],
      ['Date Range', `${format(startDate, 'yyyy-MM-dd')} - ${format(endDate, 'yyyy-MM-dd')}`],
      ['Total Shifts', getTotalStats().totalShifts],
      ['Total Hours', getTotalStats().totalHours],
      ['Night Hours', getTotalStats().nightHours],
      ['Holiday Hours', getTotalStats().weekendHours], // Αλλαγή από Weekend σε Holiday
      ['Overtime Hours', getTotalStats().overtimeHours],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

    // Shifts sheet reflecting UI
    const shiftsRows: (string | number)[][] = [
      ['Date', 'Title', 'Description', 'Location', 'Start', 'End', 'Team', 'IsHoliday', 'Hours', 'NightHours']
    ];

    scheduleData
      .filter(day => day.schedules.length > 0)
      .forEach(day => {
        const dayDate = parseISO(day.date);
        day.schedules.forEach(s => {
          const start = parseISO(s.start_date);
          const end = parseISO(s.end_date);
          const hours = calculateShiftDuration(start, end);
          const nightMinutes = calculateNightMinutes(start, end);
          const night = nightMinutes / 60;
          
          // Έλεγχος για αργία (Κυριακή ή προκαθορισμένη αργία)
          const dayDate = parseISO(day.date);
          const isSunday = dayDate.getDay() === 0;
          // Προς το παρόν χρησιμοποιούμε μόνο την Κυριακή, μπορούμε να επεκτείνουμε για holidays cache
          const isHolidayDay = isSunday; // TODO: Προσθήκη ελέγχου για προκαθορισμένες αργίες αν χρειάζεται
          
          shiftsRows.push([
            format(dayDate, 'yyyy-MM-dd'),
            s.title,
            s.description || '',
            s.location || '',
            format(start, 'HH:mm'),
            format(end, 'HH:mm'),
            s.team?.name || '',
            isHolidayDay ? 'Yes' : 'No',
            hours,
            night,
          ]);
        });
      });

    const wsShifts = XLSX.utils.aoa_to_sheet(shiftsRows);
    XLSX.utils.book_append_sheet(wb, wsShifts, 'Shifts');

    const fileName = `${selectedGuardName || 'employee'}_${format(startDate, 'yyyyMMdd')}_${format(endDate, 'yyyyMMdd')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const getTotalStats = () => {
    // Aggregate base totals
    const baseTotals = scheduleData.reduce((totals, day) => ({
      totalShifts: totals.totalShifts + day.schedules.filter(s => !shouldExcludeSchedule(s.title)).length,
      totalHours: totals.totalHours + day.totalHours,
      nightHours: totals.nightHours + day.nightHours,
      weekendHours: totals.weekendHours + day.weekendHours,
      overtimeHours: 0, // computed weekly below
    }), {
      totalShifts: 0,
      totalHours: 0,
      nightHours: 0,
      weekendHours: 0,
      overtimeHours: 0,
    });

    // Weekly overtime: Monday-Sunday, only hours exceeding 40 per week
    const weeklyHours = new Map<string, number>();
    scheduleData.forEach(day => {
      const dateObj = parseISO(day.date);
      const weekStart = startOfWeek(dateObj, { weekStartsOn: 1 });
      const key = format(weekStart, 'yyyy-MM-dd');
      weeklyHours.set(key, (weeklyHours.get(key) || 0) + day.totalHours);
    });

    let overtime = 0;
    weeklyHours.forEach(total => {
      if (total > 40) overtime += (total - 40);
    });

    return { ...baseTotals, overtimeHours: overtime };
  };

  const selectedGuardName = guards.find(g => g.id === selectedGuard)?.full_name;
  const selectedGuardCode = guards.find(g => g.id === selectedGuard)?.code;
  const totalStats = getTotalStats();

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{t('customEmployeeReport') || 'Custom Employee Report'}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6" ref={reportRef}>
          {/* Controls Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Employee Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('selectEmployee') || 'Select Employee'}
              </label>
              <Popover open={comboOpen} onOpenChange={setComboOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={comboOpen}
                    className="w-full justify-between"
                  >
                    {selectedGuard
                      ? formattedGuards.find(guard => guard.id === selectedGuard)?.displayName
                      : (t('selectEmployee') || 'Select Employee')}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0 bg-background z-50" style={{ width: 'var(--radix-popover-trigger-width)' }}>
                  <Command>
                    <CommandInput placeholder={t('searchEmployee') || 'Search employee...'} />
                    <CommandList className="max-h-[200px] overflow-auto">
                      <CommandEmpty>{t('noEmployeeFound') || 'No employee found.'}</CommandEmpty>
                      <CommandGroup>
                        {formattedGuards
                          .sort((a, b) => a.displayName.localeCompare(b.displayName))
                          .map(guard => (
                            <CommandItem
                              key={guard.id}
                              value={guard.displayName}
                              onSelect={() => {
                                setSelectedGuard(guard.id);
                                setComboOpen(false);
                              }}
                              className="cursor-pointer"
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedGuard === guard.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {guard.displayName}
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('startDate') || 'Start Date'}
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP") : (t('pickStartDate') || 'Pick start date')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[560px] max-w-[90vw] p-0">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('endDate') || 'End Date'}
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "PPP") : (t('pickEndDate') || 'Pick end date')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[560px] max-w-[90vw] p-0">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    initialFocus
                    disabled={(date) => startDate ? date < startDate : false}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Report Type and Include Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('reportType') || 'Report Type'}
              </label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="shifts">{t('shifts') || 'Shifts'}</SelectItem>
                  <SelectItem value="workingHours">{t('workingHours') || 'Working Hours'}</SelectItem>
                  <SelectItem value="attendance">{t('attendance') || 'Attendance'}</SelectItem>
                  <SelectItem value="performance">{t('performance') || 'Performance'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('includeDetails') || 'Include Details'}
              </label>
              <div className="flex flex-wrap gap-2">
                {['hours', 'overtime', 'alerts', 'leave', 'reports'].map(detail => (
                  <Button
                    key={detail}
                    variant={includeDetails.includes(detail) ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleIncludeDetailsChange(detail)}
                  >
                    {t(detail) || detail}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={fetchScheduleData}
              disabled={isLoading || !selectedGuard || !startDate || !endDate}
              variant="outline"
            >
              <Eye className="h-4 w-4 mr-2" />
              {isLoading ? (t('loading') || 'Loading...') : (t('viewSchedule') || 'View Schedule')}
            </Button>
            <Button
              onClick={handleExportPDF}
              disabled={!isViewing || scheduleData.length === 0}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('exportPdf') || 'Export PDF'}
            </Button>
            <Button
              onClick={handleExportExcel}
              disabled={!isViewing || scheduleData.length === 0}
              variant="outline"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('exportExcel') || 'Export Excel'}
            </Button>
          </div>

          {/* Report Summary */}
          {isViewing && selectedGuardName && startDate && endDate && (
            <div className="bg-muted/50 p-4 rounded-lg">
              <h3 className="font-medium mb-2">{t('reportSummary') || 'Report Summary'}</h3>
              <div className="text-sm space-y-1">
                <p><strong>{t('employee') || 'employee'}:</strong> {selectedGuardName}</p>
                <p><strong>{t('employeeCode') || 'employeeCode'}:</strong> {selectedGuardCode}</p>
                <p><strong>{t('dateRange') || 'dateRange'}:</strong> {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}</p>
                <p><strong>{t('reportType') || 'reportType'}:</strong> {reportType}</p>
                <p><strong>{t('includeDetails') || 'includeDetails'}:</strong> {includeDetails.join(', ')}</p>
              </div>
            </div>
          )}

          {/* Statistics Cards */}
          {isViewing && scheduleData.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <CalendarDays className="h-8 w-8 mx-auto mb-2 text-primary" />
                  <div className="text-2xl font-bold">{totalStats.totalShifts}</div>
                  <div className="text-sm text-muted-foreground">{t('totalShifts') || 'Total Shifts'}</div>
                </CardContent>
              </Card>
                 <Card>
                <CardContent className="p-4 text-center">
                  <Clock className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                  <div className="text-2xl font-bold">{formatHoursAndMinutes(Math.round(totalStats.totalHours * 60))}</div>
                  <div className="text-sm text-muted-foreground">{t('totalHours') || 'Total Hours'}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Moon className="h-8 w-8 mx-auto mb-2 text-purple-500" />
                  <div className="text-2xl font-bold">{totalStats.nightHours}</div>
                  <div className="text-sm text-muted-foreground">{t('nightHours') || 'Night Hours'}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Sun className="h-8 w-8 mx-auto mb-2 text-orange-500" />
                  <div className="text-2xl font-bold">{formatHoursAndMinutes(Math.round(totalStats.weekendHours * 60))}</div>
                  <div className="text-sm text-muted-foreground">{t('holidayHours') || 'Ώρες Αργιών'}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Clock className="h-8 w-8 mx-auto mb-2 text-red-500" />
                  <div className="text-2xl font-bold">{formatHoursAndMinutes(Math.round(totalStats.overtimeHours * 60))}</div>
                  <div className="text-sm text-muted-foreground">{t('overtimeHours') || 'Overtime Hours'}</div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Schedule Details */}
          {isViewing && scheduleData.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">{t('scheduleDetails') || 'Schedule Details'}</h3>
              <div className="space-y-4">
                {scheduleData
                  .filter(day => day.schedules.length > 0)
                  .map(day => (
                    <Card key={day.date}>
                      <CardHeader className="pb-3">
                        <div className="flex justify-between items-center">
                          <CardTitle className="text-base">
                            {format(parseISO(day.date), 'EEEE, MMMM d, yyyy')}
                          </CardTitle>
                           <div className="flex gap-2">
                             {day.totalHours > 0 && <Badge variant="outline">{formatHoursAndMinutes(Math.round(day.totalHours * 60))} total</Badge>}
                             {day.nightHours > 0 && <Badge variant="secondary">{day.nightHours}h night</Badge>}
                             {day.weekendHours > 0 && <Badge variant="secondary">{formatHoursAndMinutes(Math.round(day.weekendHours * 60))} αργίες</Badge>}
                             {day.overtimeHours > 0 && <Badge variant="destructive">{formatHoursAndMinutes(Math.round(day.overtimeHours * 60))} overtime</Badge>}
                           </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-2">
                          {day.schedules.map(schedule => (
                            <div key={schedule.id} className="flex justify-between items-center p-3 bg-muted/30 rounded">
                              <div>
                                <div className="font-medium">{schedule.title}</div>
                                <div className="text-sm text-muted-foreground">{schedule.description}</div>
                                <div className="text-sm text-muted-foreground">{schedule.location}</div>
                              </div>
                              <div className="text-right">
                                <div className="font-medium">
                                  {format(parseISO(schedule.start_date), 'HH:mm')} - {format(parseISO(schedule.end_date), 'HH:mm')}
                                </div>
                                <div className="text-sm text-muted-foreground">{schedule.team?.name}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
